import { useEffect, useContext, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Icon, Stack, ThemeProvider, Toggle } from "@fluentui/react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import styles from "./Layout.module.css";
import { darkTheme, lightTheme } from "../../themes";

import qglogo from "../../assets/queensland-government-logo.png";
import qtlogo from "../../assets/queensland-treasury-logo.png";

import { usePersistentState } from "../../hooks/usePersistentState";
import { handleError } from "../../toaster";
import { getToken } from "../../authConfig";
import { GetLandingInfoResponse, UserData, getLandingInfoData } from "../../api";
import { FeedbackForm } from "../../components/FeedbackForm";
import { UserInfoMenu } from "../../components/UserInfoMenu";
import { UserContext } from "../../components/UserContext";
import { WhatsNewModal } from "../../components/WhatsNewModal";
import { NavMenu } from "../../components/NavMenu";

const Layout = () => {
    const client = useMsal().instance;
    const userContext = useContext(UserContext);
    const userData = userContext?.userData as UserData;
    const { useDarkMode, setUseDarkMode } = useContext(UserContext);
    const [appVersion, setAppVersion] = useState<number | null>(null);
    const [localAppVersion, setLocalAppVersion] = usePersistentState<number>("appVersion", 0.0);
    const [environment, setEnvironment] = useState<string>();
    const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const [isFeedbackCompleted, setIsFeedbackCompleted] = useState(false);
    const [termsUrl, setTermsUrl] = useState<string>("");
    const [supportUrl, setSupportUrl] = useState<string>("");
    const location = useLocation();
    const navigate = useNavigate();
    // Hide FeedbackForm and WhatsNewModal on Landing page
    const hideOnRoutes = ["/Landing", "/FAQ", "/ViewDocument"];
    const shouldHidePopups = hideOnRoutes.includes(location.pathname);

    const handleActivity = () => {
        setLastActivity(Date.now());
    };

    const checkInactivity = () => {
        if (Date.now() - lastActivity > 5 * 60 * 1000 && !isFeedbackCompleted) { // 5 minutes of inactivity
            setIsFeedbackFormOpen(true);
        }
    };

    const handleWhatsNewDismiss = () => {
        // Store current version local storage
        if (appVersion) {
            setLocalAppVersion(appVersion);
        }
    };

    useEffect(() => {
        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
        events.forEach(event => document.addEventListener(event, handleActivity));
        const inactivityInterval = setInterval(checkInactivity, 10000); // check every 10 seconds

        return () => {
            events.forEach(event => document.removeEventListener(event, handleActivity));
            clearInterval(inactivityInterval);
        };
    }, [lastActivity, isFeedbackCompleted]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && !isFeedbackCompleted) {
                setIsFeedbackFormOpen(true);
            }
        };

        const handleMouseLeave = (e: MouseEvent) => {
            // Only trigger when mouse leaves the window from the top (address bar area)
            if (e.clientY <= 0 && !isFeedbackCompleted) {
                setIsFeedbackFormOpen(true);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        document.addEventListener("mouseleave", handleMouseLeave);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            document.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [isFeedbackCompleted]);

    const handleFeedbackFormDismiss = () => {
        setIsFeedbackFormOpen(false);
        setIsFeedbackCompleted(true);
    };

    // Set anchor colours based on theme mode
    useEffect(() => {
        if (useDarkMode) {
            document.documentElement.style.setProperty("--link-color", "#2899f5");
        } else {
            document.documentElement.style.setProperty("--link-color", "#0000ff");
        }
    }, [useDarkMode]);
    
    // Check if the user has accepted the terms
    useEffect(() => {
        // If the user hasn't accepted the terms, redirect them to the Landing route
        const allowedPaths = ["/Landing", "/FAQ"];
        if (!userData?.tou_accepted && !allowedPaths.includes(location.pathname)) {
            const currentUrl = location.pathname + location.search + location.hash;  // Capture the current URL
            sessionStorage.setItem("preLandingUrl", currentUrl);  // Store the URL
            navigate("/Landing");
        }
    }, [userData, location, navigate]);

    useEffect(() => {
        async function init() {
            const token = client ? await getToken(client) : undefined;
            getLandingInfoData(token)
                .then((response: GetLandingInfoResponse) => {
                    setAppVersion(response.APP_VERSION);
                    setEnvironment(response.ENVIRONMENT);
                    setTermsUrl(response.TERMS_URL);
                    setSupportUrl(response.SUPPORT_URL);
                })
                .catch(err => handleError(err, "Failed to fetch LandingInfoData"));
        }
        init();
    }, []);

    useEffect(() => {
        setIsFeedbackCompleted(userData.feedback_completed || false);
    }, [userData]);

    const bannerClass = environment && environment.toLowerCase() === "dev" ? styles.devBanner : styles.prodBanner;

    return (
        <ThemeProvider className={styles.layout} theme={useDarkMode ? darkTheme : lightTheme}>
            <header className={styles.header} role={"banner"}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerLeft}>
                        <div className={styles.headerTitleContainer}>
                            <img src={qglogo} className={`${styles.headerLogoQg} ${useDarkMode ? styles.headerLogoDark : ""}`} />
                            <img src={qtlogo} className={`${styles.headerLogoQt} ${useDarkMode ? styles.headerLogoDark : ""}`} />
                        </div>
                    </div>
                    <div className={styles.headerCentre}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={styles.toggleContainer}>
                            <Icon iconName="Sunny" />
                            <Toggle checked={useDarkMode} onChange={() => setUseDarkMode(!useDarkMode)} />
                            <Icon iconName="ClearNight" />
                        </Stack>
                    </div>
                    <div className={styles.headerRight}>
                        <NavMenu supportUrl={supportUrl} termsUrl={termsUrl} />
                        <UserInfoMenu userData={userData} />
                    </div>
                </div>
            </header>
            <div className={`${styles.raibanner} ${bannerClass}`}>
                <span className={styles.raiwarning}>AI-generated content may be incorrect</span>
            </div>
            <ToastContainer
                className={styles.toastContainer}
                position="top-left"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            {!shouldHidePopups && (
                <>
                    <FeedbackForm
                        isOpen={isFeedbackFormOpen}
                        onDismiss={handleFeedbackFormDismiss}
                    />
                    {appVersion !== null && localAppVersion !== appVersion && (
                        <WhatsNewModal
                            viewed={localAppVersion === appVersion}
                            onDismiss={handleWhatsNewDismiss}
                        />
                    )}
                </>
            )}
            <Outlet />
        </ThemeProvider>
    );
};

export default Layout;